/**
 * useBooks Hook
 * =============
 * Fetches and caches book data using React Query.
 * Handles loading, error, and pagination states.
 *
 * React Query gives us:
 *   - Automatic caching (no redundant Firestore reads)
 *   - Background refetching
 *   - Optimistic updates for instant UI feedback
 *   - Automatic retry on network failure
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import {
  fetchBook,
  fetchBooks,
  fetchMyBooks,
  createBook,
  updateBook,
  softDeleteBook,
} from "@/features/books/booksService";
import type { BookFilters, BookSummary } from "@/types";

// ─── QUERY KEYS ──────────────────────────────────────────────────────────────
// Centralised query keys prevent stale cache bugs.
export const bookKeys = {
  all:     ["books"]                              as const,
  lists:   () => [...bookKeys.all, "list"]        as const,
  list:    (filters: BookFilters) =>
             [...bookKeys.lists(), filters]        as const,
  details: () => [...bookKeys.all, "detail"]      as const,
  detail:  (id: string) =>
             [...bookKeys.details(), id]           as const,
  mine:    (uid: string) =>
             [...bookKeys.all, "mine", uid]        as const,
};

// ─── useBook (single book) ────────────────────────────────────────────────────
export function useBook(bookId: string) {
  return useQuery({
    queryKey:  bookKeys.detail(bookId),
    queryFn:   async () => {
      const result = await fetchBook(bookId);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    },
    enabled:   Boolean(bookId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ─── useBooks (paginated list) ────────────────────────────────────────────────
export function useBooks(filters: BookFilters) {
  return useInfiniteQuery({
    queryKey:    bookKeys.list(filters),
    queryFn:     async ({ pageParam }) => {
      const result = await fetchBooks({
        filters,
        cursor: pageParam ?? null,
      });
      if (!result.ok) throw new Error(result.message);
      return result.data;
    },
    initialPageParam: null as any,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime:        2 * 60 * 1000, // 2 minutes
    select: (data) => ({
      ...data,
      // Flatten pages into a single array for easy rendering
      books: data.pages.flatMap((p) => p.books),
    }),
  });
}

// ─── useMyBooks ───────────────────────────────────────────────────────────────
export function useMyBooks(userId: string | null) {
  return useQuery({
    queryKey:  bookKeys.mine(userId ?? ""),
    queryFn:   async () => {
      const result = await fetchMyBooks(userId!);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    },
    enabled:   Boolean(userId),
    staleTime: 60 * 1000, // 1 minute
  });
}

// ─── useCreateBook ────────────────────────────────────────────────────────────
export function useCreateBook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createBook,
    onSuccess: (result, variables) => {
      if (!result.ok) return;
      // Invalidate book lists so new book appears immediately
      queryClient.invalidateQueries({ queryKey: bookKeys.lists() });
      // Invalidate "my books" for this user
      queryClient.invalidateQueries({
        queryKey: bookKeys.mine(variables.ownerId),
      });
    },
  });
}

// ─── useUpdateBook ────────────────────────────────────────────────────────────
export function useUpdateBook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: {
      id:      string;
      updates: Parameters<typeof updateBook>[1];
    }) => updateBook(id, updates),

    onSuccess: (result, { id }) => {
      if (!result.ok) return;
      queryClient.invalidateQueries({ queryKey: bookKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: bookKeys.lists() });
    },
  });
}

// ─── useDeleteBook ────────────────────────────────────────────────────────────
export function useDeleteBook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bookId: string) => softDeleteBook(bookId),
    onSuccess: (result, bookId) => {
      if (!result.ok) return;
      // Remove from cache immediately (optimistic)
      queryClient.removeQueries({ queryKey: bookKeys.detail(bookId) });
      queryClient.invalidateQueries({ queryKey: bookKeys.lists() });
    },
  });
}

// ─── useBookSearch (debounced client-side search helper) ──────────────────────
export function filterBooks(
  books: BookSummary[],
  query: string
): BookSummary[] {
  if (!query.trim()) return books;
  const q = query.toLowerCase();
  return books.filter(
    (b) =>
      b.title.toLowerCase().includes(q) ||
      b.author.toLowerCase().includes(q) ||
      b.category.toLowerCase().includes(q)
  );
}
